import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useS } from '../../store/game';
import { seatPoints } from '../../engine/view';
import { coalSeats } from '../../engine/election';
import { PC } from '../../engine/rules';
import { PARTIES } from '../../content/parties';
import { NEED } from '../../content/consts';

const PHEX = (id: string) => id === 'YOU' ? '#d6aa4e' : PARTIES[id].c;
let lastAnimated: string | null = null;

/** Plenarsaal mit 630 Sitzen als InstancedMesh. Bei einer Abstimmung färben sich die Sitze nacheinander. */
export function Plenum3D() {
  const S = useS();
  const wrap = useRef<HTMLDivElement>(null);
  const V = S.lastVote && S.m - S.lastVote.m <= 1 ? S.lastVote : null;
  const key = `${S.res?.inP?.join(',')}|${V ? V.id : ''}`;

  useEffect(() => {
    const el = wrap.current; if (!el) return;
    let r: THREE.WebGLRenderer;
    try { r = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' }); } catch { return; }
    r.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    const scene = new THREE.Scene(), cam = new THREE.PerspectiveCamera(44, 2, .1, 100);
    scene.add(new THREE.HemisphereLight(0xfff2d8, 0x0b0f12, .9));
    const d = new THREE.DirectionalLight(0xffe2a8, .9); d.position.set(3, 6, 4); scene.add(d);
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(.11, .07, .09), new THREE.MeshStandardMaterial({ roughness: .6, metalness: .1 }), 630);
    scene.add(mesh);
    const floor = new THREE.Mesh(new THREE.CircleGeometry(3.6, 64, 0, Math.PI), new THREE.MeshStandardMaterial({ color: 0x1b2126, roughness: 1 })); floor.rotation.x = -Math.PI / 2; floor.position.y = -.05; scene.add(floor);
    const ros = new THREE.Mesh(new THREE.BoxGeometry(.9, .22, .35), new THREE.MeshStandardMaterial({ color: 0x8c6a24, roughness: .5, metalness: .4 })); ros.position.set(0, .11, .15); scene.add(ros);
    for (let i = 1; i < 10; i++) { const ring = new THREE.Mesh(new THREE.RingGeometry(1.25 + i * .26, 1.27 + i * .26, 96, 1, 0, Math.PI), new THREE.MeshBasicMaterial({ color: 0x2b353b, side: THREE.DoubleSide })); ring.rotation.x = -Math.PI / 2; ring.position.y = .01 + i * .045; scene.add(ring); }
    const pts = seatPoints(), o = new THREE.Object3D(), col = new THREE.Color();
    const base: string[] = [];
    pts.forEach((p: any, i: number) => { const rr = Math.hypot(p.x, p.y), tier = Math.round((rr - .42) / (.58 / 9)); const R = 1.3 + tier * .26, y = .06 + tier * .045; o.position.set(p.x / rr * R, y, -p.y / rr * R); o.lookAt(0, y, 0); o.updateMatrix(); mesh.setMatrixAt(i, o.matrix); base[i] = PHEX(p.pid); });
    mesh.instanceMatrix.needsUpdate = true;
    const cur = base.slice();
    const paint = () => { cur.forEach((h, i) => { col.set(h); mesh.setColorAt(i, col); }); if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true; };
    paint();
    const rm = matchMedia('(prefers-reduced-motion:reduce)').matches;
    let vote: { target: string[]; t0: number; dur: number } | null = null;
    if (V) { const target = pts.map((p: any) => V.by[p.pid] ? '#6fd394' : '#f07361'); if (V.id !== lastAnimated && !rm) { lastAnimated = V.id; vote = { target, t0: performance.now(), dur: 2600 }; } else { lastAnimated = V.id; target.forEach((t: string, i: number) => cur[i] = t); paint(); } }
    el.appendChild(r.domElement);
    const t0 = performance.now(); let raf = 0, alive = true;
    const frame = (now: number) => {
      if (!alive) return;
      const W = el.clientWidth || 300, H = 220;
      if (r.domElement.width !== Math.round(W * r.getPixelRatio())) { r.setSize(W, H, false); cam.aspect = W / H; cam.updateProjectionMatrix(); }
      const t = (now - t0) / 1000, a = rm ? 0 : Math.sin(t * .25) * .35;
      cam.position.set(Math.sin(a) * 4.9, 3.4, -Math.cos(a) * 4.9 - .6); cam.lookAt(0, .1, -1.3);
      if (vote) { const k = Math.min(1, (now - vote.t0) / vote.dur), n = Math.floor(k * 630); let ch = false; for (let i = 0; i < n; i++) if (cur[i] !== vote.target[i]) { cur[i] = vote.target[i]; ch = true; } if (ch) paint(); if (k >= 1) vote = null; }
      r.render(scene, cam); raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); r.dispose(); mesh.geometry.dispose(); if (el.contains(r.domElement)) el.removeChild(r.domElement); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <>
      <div className="plenwrap" ref={wrap}>
        <div className="plov"><b>{V ? `${V.yes} : ${630 - V.yes}` : coalSeats()}</b><span>{V ? (V.ok ? 'angenommen' : 'abgelehnt') : 'Sitze der Regierung'}</span></div>
      </div>
      {V ? <div className={'vres ' + (V.ok ? 'ok' : 'no')}><b>{V.t}</b><span>{V.ok ? 'Angenommen' : 'Abgelehnt'} mit {V.yes} Ja-Stimmen</span></div>
        : <span className="sub">Die nächste Abstimmung wird hier live ausgezählt. {S.minority ? `Euch fehlen ${NEED - coalSeats()} Stimmen zur Mehrheit.` : `Mehrheit: ${coalSeats()} von 630.`}</span>}
    </>
  );
}

/** SVG-Fallback, falls WebGL fehlt. */
export function PlenumSVG() {
  const S = useS(); const pts = seatPoints();
  const V = S.lastVote && S.m - S.lastVote.m <= 1 ? S.lastVote : null;
  return (
    <svg className="hemi plen" viewBox="-104 -4 208 110" role="img" aria-label="Plenarsaal">
      {pts.map((p: any, i: number) => <circle key={i} cx={(p.x * 100).toFixed(1)} cy={(100 - p.y * 100).toFixed(1)} r="1.6" style={{ ['--pc' as any]: PC(p.pid) }} className={V ? (V.by[p.pid] ? 'y' : 'n') : ''} />)}
      <text x="0" y="97" textAnchor="middle" className="pl-num">{V ? `${V.yes} : ${630 - V.yes}` : coalSeats()}</text>
      <text x="0" y="104" textAnchor="middle" className="pl-cap">{V ? (V.ok ? 'angenommen' : 'abgelehnt') : 'Sitze der Regierung'}</text>
    </svg>
  );
}

export function webglOk() { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch { return false; } }
